import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  Provider,
} from '@angular/core';
import { ResolveFn } from '@angular/router';

import { TranslationRepository } from './domain/interfaces/translation.interface';
import { MockTranslationRepository } from './infrastructure/repositories/translation.mock.repository';
import { HttpTranslationRepository } from './infrastructure/repositories/translation.http.repository';
import { I18N_CONFIG, I18nConfig } from './infrastructure/i18n.config';
import { TranslationsService } from './application/translations.service';
import { TranslationFacade } from './application/translation.facade';
import { TranslationMap } from './domain/translation.types';

/**
 * provideI18n — wires the i18n subsystem.
 *
 * Binds `TranslationRepository` to the mock OR http implementation based on
 * `I18N_CONFIG.useMock` (default: mock). Optionally override the config.
 *
 * Mounted at the TRUE application root (`app.config.ts`), NOT in a route's
 * providers: `TranslationFacade` is injected by root-scoped `@Service()` classes
 * (`OrgErrorMapper`), and a route-level injector is invisible to those — see the
 * note in `app.config.ts`. Every route injector below the root still shares the
 * one instance registered here.
 */
export function provideI18n(configOverride?: Partial<I18nConfig>): EnvironmentProviders {
  const providers: Provider[] = [
    MockTranslationRepository,
    HttpTranslationRepository,
    {
      provide: TranslationRepository,
      useFactory: () =>
        inject(I18N_CONFIG).useMock
          ? inject(MockTranslationRepository)
          : inject(HttpTranslationRepository),
    },
    // service + facade live in the v2 injector (not root) so they resolve the
    // route-scoped repository above.
    TranslationsService,
    TranslationFacade,
  ];

  if (configOverride) {
    providers.push({
      provide: I18N_CONFIG,
      useFactory: (): I18nConfig => ({ ...inject(I18N_CONFIG, { skipSelf: true }), ...configOverride }),
    });
  }

  return makeEnvironmentProviders(providers);
}

/**
 * translationsResolver — guarantees translations are loaded before a route
 * activates (so deep-links that skip the splash still render translated).
 * Idempotent: a no-op if the splash already initialised the facade.
 */
export const translationsResolver: ResolveFn<TranslationMap> = () =>
  inject(TranslationFacade).ensureInitialized();
